import React from 'react';
import javaIcon from '../../assets/svg/experience/java.svg';
import vscodeIcon from '../../assets/svg/experience/vscode.svg';
import androidStudioIcon from '../../assets/svg/experience/android-studio.svg';
import figmaIcon from '../../assets/svg/experience/figma.svg';
import camtasiaIcon from '../../assets/svg/experience/camtasia.svg';
import autocadIcon from '../../assets/svg/experience/autocad.svg';
import javascriptIcon from '../../assets/svg/experience/javascript.svg';
import reactIcon from '../../assets/svg/experience/react.svg';
import tailwindIcon from '../../assets/svg/experience/tailwindcss.svg';

/**
 * Modern SVG icon resolver.
 * - Brand icons load from the Simple Icons CDN (latest official, brand-colored SVGs).
 * - Non-brand / generic items use inline modern SVGs.
 */

const CDN = (slug, color) =>
   `https://cdn.simpleicons.org/${slug}${color ? `/${color}` : ''}`;

// Map skill name -> { type, slug | render }
const ICONS = {
   // Mobile / Languages
   Kotlin: { src: CDN('kotlin') },
   Java: { src: javaIcon },
   'C++': { src: CDN('cplusplus') },
   'C++ (NDK)': { src: CDN('cplusplus') },
   C: { src: CDN('c') },
   Go: { src: CDN('go') },
   'Android SDK': { src: CDN('android') },
   'Android Studio': { src: androidStudioIcon },
   AdMob: { render: 'admob' },
   XML: { render: 'xml' },

   // Backend / Security
   Python: { src: CDN('python') },
   'Node.js': { src: CDN('nodedotjs') },
   MongoDB: { src: CDN('mongodb') },
   'App Security': { render: 'security' },

   // Tools
   Git: { src: CDN('git') },
   GitHub: { src: CDN('github', '0bc97a') },
   Postman: { src: CDN('postman') },
   NPM: { src: CDN('npm') },
   'VS Code': { src: vscodeIcon },
   Netlify: { src: CDN('netlify') },
   Figma: { src: figmaIcon },
   Photoshop: { render: 'photoshop' },
   AutoCAD: { src: autocadIcon },
   Office: { render: 'office' },
   PHP: { src: CDN('php') },
   cPanel: { src: CDN('cpanel') },
   Cloudflare: { src: CDN('cloudflare') },
   Docker: { src: CDN('docker') },
   Termius: { src: CDN('termius', '0bc97a') },
   GCP: { src: CDN('googlecloud') },
   'Google Cloud': { src: CDN('googlecloud') },
   Camtasia: { src: camtasiaIcon },

   // Web
   JavaScript: { src: javascriptIcon },
   React: { src: reactIcon },
   MySQL: { src: CDN('mysql') },
   'Tailwind CSS': { src: tailwindIcon },
};

/* ---------- Inline modern SVGs for non-brand items ---------- */
const Inline = {
   java: (
      <svg viewBox="0 0 24 24" className="h-full w-full">
         <rect width="24" height="24" rx="4" fill="#007396"/>
         <text x="2" y="11" fill="#f89820" fontSize="7" fontWeight="bold" fontFamily="Arial, sans-serif">JAVA</text>
         <path d="M8 13c0 0 1.5 1 4 1s4-1 4-1" stroke="#f89820" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
         <path d="M9 15.5c0 0 1 .8 3 .8s3-.8 3-.8" stroke="#f89820" strokeWidth="1" strokeLinecap="round" fill="none"/>
         <path d="M10.5 17.5c0 0 .5.5 1.5.5s1.5-.5 1.5-.5" stroke="#f89820" strokeWidth="0.9" strokeLinecap="round" fill="none"/>
      </svg>
   ),
   vscode: (
      <svg viewBox="0 0 100 100" className="h-full w-full">
         <rect width="100" height="100" rx="16" fill="#0065A9"/>
         <path d="M70.9 13.1L50.2 35.5 32.8 20.9l-9.1 4.6v49.9l9.1 4.6 17.4-14.6 20.7 22.4 15.4-7V20.1l-15.4-7zM70.9 69.4L53.4 50l17.5-19.4v38.8z" fill="white"/>
      </svg>
   ),
   admob: (
      <svg viewBox="0 0 24 24" className="h-full w-full" fill="none">
         {/* Google color dots — AdMob brand mark */}
         <circle cx="12" cy="5" r="3.2" fill="#4285F4"/>
         <circle cx="19" cy="12" r="3.2" fill="#EA4335"/>
         <circle cx="12" cy="19" r="3.2" fill="#FBBC05"/>
         <circle cx="5" cy="12" r="3.2" fill="#34A853"/>
         <circle cx="12" cy="12" r="2" fill="#EA4335" opacity="0.85"/>
      </svg>
   ),
   xml: (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
         <defs>
            <linearGradient id="xmlG" x1="0" y1="0" x2="1" y2="1">
               <stop offset="0%" stopColor="#0bc97a" />
               <stop offset="100%" stopColor="#6366f1" />
            </linearGradient>
         </defs>
         <path
            d="M9 7l-4 5 4 5M15 7l4 5-4 5M13 5l-2 14"
            stroke="url(#xmlG)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
         />
      </svg>
   ),
   security: (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
         <defs>
            <linearGradient id="secG" x1="0" y1="0" x2="0" y2="1">
               <stop offset="0%" stopColor="#21e394" />
               <stop offset="100%" stopColor="#067f51" />
            </linearGradient>
         </defs>
         <path
            d="M12 2.5l7.5 3v6c0 4.6-3.2 8.6-7.5 9.5-4.3-.9-7.5-4.9-7.5-9.5v-6L12 2.5z"
            fill="url(#secG)"
            opacity="0.95"
         />
         <path
            d="M9 12.2l2 2 4-4.4"
            stroke="white"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
         />
      </svg>
   ),
   photoshop: (
      <svg viewBox="0 0 24 24" className="h-full w-full">
         <rect width="24" height="24" rx="4" fill="#001E36"/>
         <text x="3" y="17" fill="#31A8FF" fontSize="12" fontWeight="bold" fontFamily="Georgia, 'Times New Roman', serif">Ps</text>
      </svg>
   ),
   office: (
      <svg viewBox="0 0 24 24" className="h-full w-full">
         <rect x="1.5" y="1.5" width="9.5" height="9.5" rx="1.5" fill="#F25022"/>
         <rect x="13" y="1.5" width="9.5" height="9.5" rx="1.5" fill="#7FBA00"/>
         <rect x="1.5" y="13" width="9.5" height="9.5" rx="1.5" fill="#00A4EF"/>
         <rect x="13" y="13" width="9.5" height="9.5" rx="1.5" fill="#FFB900"/>
      </svg>
   ),
   camtasia: (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
         <defs>
            <linearGradient id="camG" x1="0" y1="0" x2="1" y2="1">
               <stop offset="0%" stopColor="#7c3aed" />
               <stop offset="100%" stopColor="#0bc97a" />
            </linearGradient>
         </defs>
         <rect x="3" y="6" width="14" height="12" rx="3" fill="url(#camG)" />
         <path d="M17 10l4-2v8l-4-2v-4z" fill="url(#camG)" />
         <path d="M9 10.5v3l3-1.5-3-1.5z" fill="white" />
      </svg>
   ),
};

const SkillIcon = ({ name, className = '' }) => {
   const def = ICONS[name];
   if (!def) {
      return (
         <div className={className}>
            <div className="h-full w-full grid place-items-center text-[10px] font-bold text-zinc-500">
               {name?.charAt(0)}
            </div>
         </div>
      );
   }
   if (def.src) {
      return (
         <div className={className}>
            <img
               src={def.src}
               alt={name}
               loading="lazy"
               className="h-full w-full object-contain"
            />
         </div>
      );
   }
   return <div className={className}>{Inline[def.render]}</div>;
};

export default SkillIcon;
